import * as React from 'react';
import { Table, IconButton } from '@ixicare/ui';
import { Link } from 'react-router-dom';
import { Map, SquarePen, Trash2 } from 'lucide-react';
import DeletePlaneConfirmationModal from '~containers/Plane/DeletePlaneConfirmationModal';
import SiteStore from '../store/Site';
import { ChangeFloorNameModal } from './SiteModals/ChangeFloorNameModal';
import { PlaneEntryProps } from '@ixicare/resources';
import { observer } from 'mobx-react';
import { t } from '~i18n';

interface SiteSettingsTableProps {
  site: SiteStore;
}

const SiteSettingsTable: React.FC<SiteSettingsTableProps> = ({ site }) => {
  const [showModal, setShowModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [planeUuid, setPlaneUuid] = React.useState('');
  const [floorDataModal, setFloorDataModal] = React.useState<PlaneEntryProps>();

  const toggleModal = React.useCallback(
    (uuid) => () => {
      setShowModal(!showModal);
      setPlaneUuid(uuid);
    },
    []
  );

  const handleRefresh = () => {
    site.refresh();
  };

  const handleDelete = (uuid: string) => {
    return new Promise<void>(() => {
      setLoading(true);
      site
        .archivePlane(uuid)
        .then(() => {
          setLoading(false);
          setShowModal(!showModal);
        })
        .catch((res) => {
          setLoading(false);
          console.error(res);
        });
    });
  };

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('site.floors.name')}</Table.HeadCol>
            <Table.HeadCol>{t('site.floors.view')}</Table.HeadCol>
            <Table.HeadCol>{t('site.floors.edit')}</Table.HeadCol>
            <Table.HeadCol>{t('site.floors.delete')}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {site.planes.map((floor) => (
            <Table.Row key={`siteSettings-table-row-${floor.uuid}`}>
              <Table.Col>
                {floor.floor} - {floor.floorName}{' '}
                <IconButton lucide={SquarePen} smallIcon={true} onClick={() => setFloorDataModal(floor)} />
              </Table.Col>
              <Table.Col>
                <Link to={`/sites/${site.uuid}/${floor.id}`}>
                  <IconButton lucide={Map} />
                </Link>
              </Table.Col>
              <Table.Col>
                {!!floor.cdnUrl && floor.isPlaneImageProcessed && (
                  <Link to={`/sites/${site.uuid}/${floor.id}/edit`}>
                    <IconButton lucide={SquarePen} />
                  </Link>
                )}
              </Table.Col>
              <Table.Col>
                <IconButton lucide={Trash2} onClick={toggleModal(floor.uuid)} />
              </Table.Col>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {showModal && (
        <DeletePlaneConfirmationModal
          setShowModal={setShowModal}
          loading={loading}
          onDelete={handleDelete}
          planeUuid={planeUuid}
        />
      )}
      {floorDataModal && (
        <ChangeFloorNameModal
          site={site}
          floor={floorDataModal}
          onRefresh={handleRefresh}
          onClose={() => setFloorDataModal(undefined)}
        />
      )}
    </>
  );
};

export default observer(SiteSettingsTable);
